import { Divider, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { InspectionActivity } from '../../../types/plan';
import { User } from '../../../types/user';
import { Avatar, Chip, SearchBar } from '../../global';
import { CheckInput } from '../../global/CheckInput';

const useComponentClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        margin: '-7.5px 0',
        '& > div': {
            margin: '7.5px 0',
        },
    },
});

/**
 * List the project tasks and allow users to be assigned for each task.
 */
export const Tasks = () => {
    const classes = useComponentClasses();
    const dispatch = useTypedDispatch();
    const { project, users } = useTypedSelector(state => ({ project: state.project.changed, users: state.user.users }));

    React.useEffect(() => void dispatch(actions.user.get()), []);

    if (!project) return <span>{'#project not in store'}</span>;

    return (
        <div className={classes.root}>
            {[...Object.entries(project.methods)]
                .sort((a, b) => -a[0].localeCompare(b[0]))
                .map(([type, methods]) => (
                    <Activity
                        key={type}
                        type={type as 'internal' | 'external'}
                        methods={methods ?? {}}
                        preparation={project.preparation_steps[type as 'internal' | 'external'] ?? []}
                        user={users.find(u => u.id === project.user)}
                        users={users}
                        setUser={user => user && dispatch(actions.project.edit({ user: user.id }))}
                    />
                ))}
        </div>
    );
};

const useActivityClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 10px',
        borderRadius: 5,
        '& > span': {
            color: theme.colors.neutral[1],
            ...theme.typography.headings.h2,
            marginBottom: 0,
            textTransform: 'capitalize',
        },
    },
});

/**
 * Component to show inspection activity details.
 */
const Activity = (props: {
    type: InspectionActivity['type'];
    preparation: string[];
    methods: { [name: string]: string[] };
    users: User[];
    user?: User;
    setUser: (user?: User) => void;
}) => {
    const classes = useActivityClasses();
    const { t } = usePrefixTranslation('project', { project: 'scheduler.Tasks' });

    return (
        <div className={classes.root}>
            <span>{t(`inspection-${props.type}`)}</span>
            <MethodSteps {...props} name={t('inspection-preparation')} tasks={props.preparation} />
            {Object.entries(props.methods)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([name, tasks]) => (
                    <MethodSteps key={name} {...props} name={name} tasks={tasks} />
                ))}
        </div>
    );
};

const useMethodStepsClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: 30,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        '& > span': {
            flex: 1,
            color: theme.colors.neutral[1],
            ...theme.typography.headings.h5,
            marginBottom: 12,
            textTransform: 'capitalize',
        },
    },
    divider: {
        margin: '5px 0',
        '&.internal': {
            background: theme.colors.green[4],
        },
        '&.external': {
            background: theme.colors.amber[1],
        },
    },
    steps: {
        margin: '-5px 0',
        '& > div': {
            margin: '5px 0',
        },
    },
});

/**
 * Component to list method steps, assigned users and to edit users.
 */
const MethodSteps = (props: {
    type: InspectionActivity['type'];
    name: string;
    tasks: string[];
    user?: User;
    users: User[];
    setUser: (user?: User) => void;
}) => {
    const classes = useMethodStepsClasses();
    const status = { [props.type]: true };

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <span>{props.name}</span>
                <UserSearchBar user={props.user} users={props.users} onSelect={props.setUser} />
            </div>
            <Divider className={clsx(classes.divider, status)} />
            <div className={classes.steps}>
                {props.tasks.map((step, i) => (
                    <CheckInput key={i} defaultValue={step} />
                ))}
            </div>
        </div>
    );
};

const useUserSearchBarClasses = makeStyles({
    root: {
        minWidth: '150px',
        '& .MuiFormControl-root': {
            marginBottom: 0,
        },
        '& .MuiInputBase-root': {
            padding: 0,
        },
    },
    option: {
        display: 'flex',
        alignItems: 'center',
        margin: 10,
        '& > div': {
            width: 24,
            height: 24,
        },
        '& > span': {
            margin: '0 4px',
            ...theme.typography.content.label.default,
        },
    },
});

export const UserSearchBar = (props: { user?: User; users: User[]; onSelect?: (user?: User) => void }) => {
    const classes = useUserSearchBarClasses();
    const [editing, setEditing] = React.useState(false);

    if (!editing && props.user)
        return (
            <Chip
                label={props.user.full_name}
                avatar={<Avatar name={props.user.full_name} src={props.user.picture} />}
                onClick={() => setEditing(true)}
            />
        );

    return (
        <SearchBar
            className={classes.root}
            options={props.users}
            renderOption={option => (
                <div className={classes.option}>
                    <Avatar name={option.full_name} src={option.picture} />
                    <span>{option.full_name}</span>
                </div>
            )}
            renderLabel={option => option.full_name}
            onSelect={option => (setEditing(false), props.onSelect?.(option))}
        />
    );
};
